/**
 * Product terms for the SE Budget feature.
 * Semantic keys: ALLOCATION = org-level pool (OrgBudget); BUDGET = org spending plan (BudgetPlan).
 * MVP: constants for every instance; later hydrate from org terminology prefs in one place.
 */

export const BudgetLabels = {
    /** Org-level pool (domain: OrgBudget). Legacy default "Budget"; future e.g. "Allocation". */
    ALLOCATION: "Budget",
    ALLOCATIONS: "Budgets",

    /** Org spending plan (domain: BudgetPlan). Legacy default "Budget Plan"; future e.g. "Budget". */
    BUDGET: "Budget Plan",
    BUDGETS: "Budget Plans",

    ALLOCATION_NAME: "Budget Name",
    ALLOCATION_DETAILS: "Budget Details",
    ALLOCATION_ALLOCATION: "Budget Allocation",
    CREATE_ALLOCATION: "Create Budget",
    EDIT_ALLOCATION: "Edit Budget",
    SAVE_ALLOCATION: "Save Budget",
    BUDGET_LINE_ITEMS: "Budget Line Items",
    BUDGET_CATEGORY: "Budget Category",
    BUDGET_CATEGORIES: "Budget Categories",
    BUDGET_CATEGORY_MANAGEMENT: "Budget Category Management",
    BUDGET_ADMINISTRATION: "Budget Administration",
    BUDGET_DASHBOARD: "Budget Dashboard",
    PENDING_BUDGET_REQUESTS: "Pending Budget Requests",
    ORGANIZATION_BUDGETS: "Organization Budgets",
    ORGANIZATION_BUDGET_STATUS: "Organization Budget Status",
    LOADING_BUDGET_DATA: "Loading budget data...",
    ALLOCATIONS_CREATED: "Budgets Created",
    LOADING_PENDING_REQUESTS: "Loading pending budget requests...",
    BUDGET_STATUS: "Budget Status",
    BUDGET_REPORTS: "Budget Reports",
} as const;

export type BudgetLabel = (typeof BudgetLabels)[keyof typeof BudgetLabels];

/** Sentences and labels composed from {@link BudgetLabels} so term flips stay centralized. */
export const BudgetLabelPhrases = {
    selectAllocation: `Select a ${BudgetLabels.ALLOCATION}`,
    enterAllocationName: `Enter ${BudgetLabels.ALLOCATION.toLowerCase()} name`,
    enterAllocationDescription: `Enter ${BudgetLabels.ALLOCATION.toLowerCase()} description (optional)`,
    allocationWorkflowHelp: `${BudgetLabels.BUDGETS} under this ${BudgetLabels.ALLOCATION.toLowerCase()} use this workflow. Leave empty to auto-approve plans when saved.`,
    createBudget: `Create ${BudgetLabels.BUDGET}`,
    editBudget: `Edit ${BudgetLabels.BUDGET}`,
    budgetManagement: `${BudgetLabels.BUDGET} Management`,
    manageBudgetsSubtitle: `Create and manage ${BudgetLabels.BUDGETS.toLowerCase()} for your organization`,
    loadingBudgets: `Loading ${BudgetLabels.BUDGETS.toLowerCase()}...`,
    noLineItemsForBudget: `No line items found for this ${BudgetLabels.BUDGET.toLowerCase()}.`,
    budgetLineItemsFor: (planName: string) => `${BudgetLabels.BUDGET_LINE_ITEMS} for ${planName}`,
    budgetsSavedAsDraftsNote: `${BudgetLabels.BUDGETS} are saved as drafts. After adding line items,`,
    allocationNoWorkflowNote: `This ${BudgetLabels.ALLOCATION.toLowerCase()} has no approval workflow configured. Plans are`,
    budgetsUnderAllocationWorkflow: (autoApproveSuffix: string) =>
        `${BudgetLabels.BUDGETS} under this ${BudgetLabels.ALLOCATION.toLowerCase()} use this workflow. ${autoApproveSuffix}`,
    budgetsUnderAllocationWorkflowAutoApprove: "Leave empty to auto-approve plans without review.",
    budgetsUnderAllocationWorkflowAutoApproveShort: "Leave empty to auto-approve",
    noBudgetsYetNeedAllocation: `This organization has no ${BudgetLabels.ALLOCATIONS.toLowerCase()} yet. A ${BudgetLabels.ALLOCATION.toLowerCase()} is required before you can add plans.`,
    noPermissionViewBudgets: `You do not have permission to view ${BudgetLabels.BUDGETS.toLowerCase()} for this organization.`,
    noPermissionViewAllocations: `You do not have permission to view ${BudgetLabels.ALLOCATIONS.toLowerCase()} for this organization.`,
    noAllocationsForOrg: `No ${BudgetLabels.ALLOCATIONS.toLowerCase()} found for this organization`,
    failedLoadBudgetData: "Failed to load budget data",
    unnamedBudget: `Unnamed ${BudgetLabels.BUDGET}`,
    failedOpenBudgetForm: `Failed to open ${BudgetLabels.BUDGET.toLowerCase()} form`,
    failedSubmitBudget: `Failed to submit ${BudgetLabels.BUDGET.toLowerCase()}`,
    budgetSubmittedSuccessfully: `${BudgetLabels.BUDGET} submitted successfully`,
    failedCreateBudget: (detail: string) => `Failed to create ${BudgetLabels.BUDGET.toLowerCase()}: ${detail}`,
    failedUpdateBudget: (detail: string) => `Failed to update ${BudgetLabels.BUDGET.toLowerCase()}: ${detail}`,
    failedSaveBudget: (isEdit: boolean) =>
        `Failed to ${isEdit ? "update" : "create"} ${BudgetLabels.BUDGET.toLowerCase()}`,
    noTodoForBudget: `No todo ID found for this ${BudgetLabels.BUDGET.toLowerCase()}`,
    budgetApprovedSuccessfully: `${BudgetLabels.BUDGET} approved successfully`,
    failedApproveBudget: `Failed to approve ${BudgetLabels.BUDGET.toLowerCase()}`,
    budgetDeniedSuccessfully: `${BudgetLabels.BUDGET} denied successfully`,
    failedDenyBudget: `Failed to deny ${BudgetLabels.BUDGET.toLowerCase()}`,
    allocationCreatedSuccessfully: `${BudgetLabels.ALLOCATION} created successfully.`,
    allocationNotFound: `${BudgetLabels.ALLOCATION} not found for this organization.`,
    allocationNameRequired: `${BudgetLabels.ALLOCATION} name is required`,
    allocationNameMinLength: `${BudgetLabels.ALLOCATION} name must be at least 3 characters`,
    firstBudgetCategoryHint: `No categories have been created yet. Click Add Category to create your first ${BudgetLabels.BUDGET_CATEGORY.toLowerCase()}.`,
    workflowSpawnOption: BudgetLabels.BUDGET,
    selectOrganizationForBudget: `Select Organization for ${BudgetLabels.BUDGET} *`,
    loadBudgetsFromOrgHint: `Select the organization to load ${BudgetLabels.BUDGETS.toLowerCase()} from.`,
    selectBudgetRequired: `Select ${BudgetLabels.BUDGET} *`,
    selectBudgetPlaceholder: `-- Select ${BudgetLabels.BUDGET} --`,
    spawnChildBudgetHint: `Select the ${BudgetLabels.BUDGET.toLowerCase()} to use for the new child workflow submission. Required`,
    budgetPlanSubmitActivity: `${BudgetLabels.BUDGET} Submission`,
} as const;
